import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LandingPage from "@/pages/LandingPage";
import IntakeFormPage from "@/pages/IntakeFormPage";
import LearningPlanPage from "@/pages/LearningPlanPage";
import LoginPage from "@/pages/LoginPage";
import AuthenticateUserPage from "@/pages/AuthenticateUserPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import MyPlansPage from "@/pages/MyPlansPage";
import ManagerPortalPage from "@/pages/ManagerPortalPage";
import SSOPage from "@/pages/SSOPage";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import { installAuthInterceptor } from "@/lib/authInterceptor";
import { startSessionKeepAlive } from "@/lib/sessionKeepAlive";

installAuthInterceptor();
startSessionKeepAlive();

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8F3] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#194449]" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

function App() {
  return (
    <div className="App min-h-screen bg-[#FFF8F3]">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            {/* LevelUp SSO callback — public, one-time token exchange */}
            <Route path="/authenticate-user" element={<AuthenticateUserPage />} />
            {/* Public SSO entry point — must NOT have an auth guard */}
            <Route path="/sso" element={<SSOPage />} />
            <Route path="/intake" element={<ProtectedRoute><IntakeFormPage /></ProtectedRoute>} />
            <Route path="/plan/:sessionId" element={<LearningPlanPage />} />
            <Route path="/my-plans" element={<ProtectedRoute><MyPlansPage /></ProtectedRoute>} />
            <Route path="/portal" element={<ProtectedRoute><ManagerPortalPage /></ProtectedRoute>} />
            <Route path="/admin/login" element={<Navigate to="/login" replace />} />
            <Route path="/admin/local-login" element={<AdminLoginPage />} />
            {/* Redirect bare /local-login to the correct hidden admin path */}
            <Route path="/local-login" element={<Navigate to="/admin/local-login" replace />} />
            <Route path="/admin" element={<AdminDashboardPage />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
